/**
 * SEO 기반 (2026-10-13 · fire-your-seo-agency Phase 1·4) — 사이트 주소 · 공개/비공개 경로 · 페이지별 고유 title·description·canonical 은 이 파일에서만.
 * 문구는 각 페이지 화면에 이미 있는 문장에서 가져왔다 (새 주장 0 · 과장 0). 원칙·절차: docs/spec/seo.md.
 */
import type { Metadata } from "next";

export const SITE_URL = "https://www.maptest.kr";
export const SITE_NAME = "MAP Test Experts";
export const SITE_LOGO = "/logo-map.png";
/**
 * 검색엔진 소유 확인 코드 (2026-10-13 · 공개 값 · 비밀 아님 — HTML meta 에 그대로 노출되는 값).
 * env (NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION) 가 있으면 env 우선 · 없으면 이 값. 대표 서치콘솔 "HTML 태그" 방식 (2026-10-13 대표 전달).
 */
export const GOOGLE_SITE_VERIFICATION = "SJria9wa2lCuKxaajnGkeEWyKlEtAxiV9dxhPk2EMMg";
/** 네이버 서치어드바이저 "HTML 태그" 확인 코드 (공개 값 · env NEXT_PUBLIC_NAVER_SITE_VERIFICATION 이 있으면 env 우선). */
export const NAVER_SITE_VERIFICATION = "b945395f7543b1df7379e08361bdd00e070ecd29";
/** GA4 측정 ID (2026-10-13 대표 전달 · 공개 값 · 페이지 스크립트에 그대로 노출 · env NEXT_PUBLIC_GA4_ID 가 있으면 env 우선). */
export const GA4_MEASUREMENT_ID = "G-89VY5DR56G";
export const DEFAULT_OG_IMAGE = { url: "/og-v2.png", width: 1200, height: 630 } as const;

/** JSON-LD 엔티티 @id — 사이트 전체에서 같은 값 (전역 1회 선언 + 참조). */
export const ORG_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;

/**
 * 공개 페이지 — 고유 메타 · sitemap 대상. 경로는 canonical (쿼리 없음).
 * 학생 화면에 보이는 문구가 아니라 검색 결과·공유 미리보기 문구다 (화면 문장 재사용).
 */
export const PAGE_META = {
  home: {
    path: "/",
    // 2026-10-26 대표 승인 문구 (주간 보고 10/19~25 후속 ④ · 검색 결과 제목·설명 · 글자 그대로 · 변경일 docs/KNOWN_FACTS.md)
    title: "MAP TEST 모의고사 | NWEA MAP Growth 방식 온라인 모의고사 - 맵테스트 엑스퍼츠",
    description: "NWEA MAP TEST와 같은 CAT 방식으로 미리 풀어 보고 RIT 점수·백분위·틀린 문제 해설을 받아 보세요. 학년별 평균 RIT와 공식 MAP TEST 신청 안내까지.",
    changeFrequency: "daily",
    priority: 1,
  },
  pricing: {
    path: "/pricing",
    title: "MAP TEST 모의고사 요금제 · 과목권 | MAP Test Experts",
    description: "MAP TEST 모의고사 과목권 요금제입니다. 결제 즉시 과목권이 충전되고 Reading · Math · Language Usage 중 원하는 과목을 자유롭게 선택해 응시할 수 있습니다.",
    changeFrequency: "weekly",
    priority: 0.8,
  },
  mapTest: {
    path: "/map-test",
    title: "NWEA MAP TEST 공식 온라인 응시 신청 | MAP Test Experts",
    description: "NWEA MAP TEST 공식 온라인 응시 신청 페이지입니다. MAP TEST EXPERTS와 함께 하는 MAP TEST는 NWEA 공식 리포트와 함께 MTE 자체 분석 리포트를 제공합니다.",
    changeFrequency: "weekly",
    priority: 0.8,
  },
  note: {
    path: "/note",
    title: "MAP TEST Navigator | MAP Test Experts",
    description: "MAP TEST에 대한 모든 정보, 여기서 확인하세요.",
    changeFrequency: "daily",
    priority: 0.9,
  },
  signup: {
    path: "/signup",
    title: "회원가입 · MAP TEST 무료 체험 | MAP Test Experts",
    description: "MAP Test Experts 회원가입 페이지입니다. 가입 후 실제 MAP TEST와 같은 화면 구성으로 무료 체험을 시작할 수 있습니다.",
    changeFrequency: "yearly",
    priority: 0.5,
  },
  partnerGuide: {
    path: "/partner/guide",
    title: "학원·선생님 파트너 이용 안내 | MAP Test Experts",
    description: "MAP Test Experts 학원·선생님 파트너 제휴 이용 안내서입니다. 학생 할인, 커미션, 정산 일정 등 파트너 제휴 조건을 확인할 수 있습니다.",
    changeFrequency: "monthly",
    priority: 0.4,
  },
  partnerApply: {
    path: "/partner/apply",
    title: "학원·선생님 파트너 제휴 신청 | MAP Test Experts",
    description: "MAP Test Experts 학원·선생님 파트너 제휴 신청 페이지입니다. 제휴 약관을 확인하고 신청서를 제출할 수 있습니다.",
    changeFrequency: "monthly",
    priority: 0.3,
  },
  partnerTerms: {
    path: "/partner/terms",
    title: "학원·선생님 파트너 제휴 약관 | MAP Test Experts",
    description: "MAP Test Experts 학원·선생님 파트너 제휴 약관 전문입니다. 제휴 조건, 커미션과 정산, 계약 해지 기준을 확인할 수 있습니다.",
    changeFrequency: "yearly",
    priority: 0.2,
  },
  privacy: {
    path: "/privacy",
    title: "개인정보처리방침 | MAP Test Experts",
    description: "MAP Test Experts 개인정보처리방침입니다. 수집하는 개인정보 항목, 이용 목적, 보유 기간과 이용자의 권리를 안내합니다.",
    changeFrequency: "yearly",
    priority: 0.2,
  },
  terms: {
    path: "/terms",
    title: "이용약관 및 환불정책 | MAP Test Experts",
    description: "MAP Test Experts 이용약관 및 환불정책입니다. 서비스 이용 조건, 결제와 환불 기준을 안내합니다.",
    changeFrequency: "yearly",
    priority: 0.2,
  },
} as const satisfies Record<string, { path: string; title: string; description: string; changeFrequency: "daily" | "weekly" | "monthly" | "yearly"; priority: number }>;

/** 색인하지 않는 페이지 (sitemap 제외 · 고유 title 만 · robots noindex). */
export const NOINDEX_PAGE_META = {
  login: { path: "/login", title: "로그인 | MAP Test Experts", description: "MAP Test Experts 로그인 페이지입니다." },
  reportError: { path: "/report-error", title: "문제 오류 신고 | MAP Test Experts", description: "MAP Test Experts 문제 오류 신고 페이지입니다." },
  refer: { path: "/refer", title: "친구 추천 | MAP Test Experts", description: "MAP Test Experts 친구 추천 페이지입니다." },
  coach: { path: "/coach", title: "MAP TEST 코치 | MAP Test Experts", description: "MAP Test Experts 코치 페이지입니다." },
} as const;

/** robots.txt Disallow — 로그인 뒤 화면 · 관리자 · 시험·결과·결제 · API · 개발 화면. 앞부분 일치라 /report 는 $·? 로 끊는다 (/report-error 는 허용). */
export const PRIVATE_PATHS = [
  "/admin", "/api/", "/app", "/auth/", "/coach", "/dashboard", "/dev/", "/mypage", "/payment", "/practice",
  "/register", "/report$", "/report?", "/reports", "/test", "/workbook", "/partner$", "/partner?",
] as const;

export function absoluteUrl(path: string): string {
  return path.startsWith("http") ? path : `${SITE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
}

/** 공개 페이지 메타 (경로·제목·설명을 직접 받음 · 질문 페이지 /guide 등 동적 페이지용). */
export function metadataFor(m: { path: string; title: string; description: string }, type: "website" | "article" = "website"): Metadata {
  const images = [{ ...DEFAULT_OG_IMAGE, alt: m.title }];
  return {
    title: m.title,
    description: m.description,
    alternates: { canonical: m.path },
    openGraph: { type, url: m.path, title: m.title, description: m.description, siteName: SITE_NAME, locale: "ko_KR", images },
    twitter: { card: "summary_large_image", title: m.title, description: m.description, images: [DEFAULT_OG_IMAGE.url] },
  };
}

/** 공개 페이지 메타 — title · description · canonical · og · twitter 를 한 번에 (페이지마다 고유). */
export function pageMetadata(key: keyof typeof PAGE_META): Metadata {
  const m = PAGE_META[key];
  const images = [{ ...DEFAULT_OG_IMAGE, alt: m.title }];
  return {
    title: m.title,
    description: m.description,
    alternates: { canonical: m.path },
    openGraph: { type: "website", url: m.path, title: m.title, description: m.description, siteName: SITE_NAME, locale: "ko_KR", images },
    twitter: { card: "summary_large_image", title: m.title, description: m.description, images: [DEFAULT_OG_IMAGE.url] },
  };
}

export function noindexMetadata(key: keyof typeof NOINDEX_PAGE_META): Metadata {
  const m = NOINDEX_PAGE_META[key];
  return { title: m.title, description: m.description, alternates: { canonical: m.path }, robots: { index: false, follow: true } };
}
